import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';
import { thinkingModes } from '../data/modes.js';
import { studyPurposes } from '../data/purposes.js';
import { toolTypes } from '../data/toolTypes.js';
import { getProfile } from '../data/profileMatrix.js';
import ScrollToTop from '../utils/ScrollToTop';

const {
  FiPenTool, FiTarget, FiCpu, FiLock,
  FiStar, FiAward, FiTool, FiCheck, FiUser, FiInfo,
} = FiIcons;

const LEARNER_COMMITMENTS = [
  'Ich erscheine zu jedem vereinbarten Termin pünktlich und vorbereitet.',
  'Ich bearbeite die vereinbarten Aufgaben zwischen den Sitzungen eigenständig.',
  'Ich dokumentiere meine Arbeit durch Annotationen, Kommentare und Notizen.',
  'Ich gebe regelmäßig eine ehrliche und kritische Selbstbewertung an.',
  'Ich akzeptiere, dass Fehler Teil des Prozesses sind – nicht Zeichen des Scheiterns.',
];

export default function LearningContract({ modeId, purposeId, toolId, onNext }) {
  const [learnerSigned, setLearnerSigned] = useState(false);

  const mode = thinkingModes[modeId] || thinkingModes.structure;
  const purpose = studyPurposes[purposeId] || studyPurposes.sprint;
  const tool = toolTypes[toolId] || toolTypes.systematic;
  const combined = getProfile(modeId, toolId);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto pt-8 pb-24 px-4"
    >
      <ScrollToTop />

      {/* Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs font-black mb-6 tracking-[0.2em] uppercase"
        >
          <SafeIcon icon={FiStar} />
          <span>Offizielles Dokument</span>
        </motion.div>
        <h2 className="text-5xl md:text-6xl font-black text-white mb-4 tracking-tighter">
          Deine{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-600 font-serif italic">
            Vertragsurkunde
          </span>
        </h2>
        <p className="text-slate-300 text-lg max-w-xl mx-auto">
          Deine Selbstverpflichtung. Der vollständige Lernplan wird dir per E-Mail zugesandt.
        </p>
      </div>

      {/* Contract Document */}
      <div className="relative group mb-10">
        <div className="absolute -inset-1 bg-gradient-to-b from-amber-500/20 to-transparent blur-2xl opacity-50 group-hover:opacity-100 transition-opacity" />
        <div className="relative bg-slate-900 border-4 border-slate-800 rounded-[40px] overflow-hidden shadow-2xl">

          {/* Document Header */}
          <div className="h-28 bg-slate-800 flex items-center justify-between px-10 border-b-2 border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white shadow-xl">
                <SafeIcon icon={FiAward} size={28} />
              </div>
              <div>
                <h3 className="text-white font-black text-xl tracking-tight uppercase">MatheMission</h3>
                <p className="text-blue-400 text-[10px] font-black tracking-widest uppercase">
                  Protocol Alpha-7 · Verbindlicher Lernvertrag
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full transition-colors ${learnerSigned ? 'bg-emerald-400' : 'bg-slate-600'}`} />
              <div className="w-3 h-3 rounded-full bg-blue-400/40" title="Wird per E-Mail bestätigt" />
            </div>
          </div>

          <div className="p-8 md:p-12 space-y-10">
            {/* Profile Summary */}
            <div className="text-center space-y-2">
              <h4 className="text-amber-500 font-serif italic text-xl">Zertifikat der Zusammenarbeit</h4>
              <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto text-sm">
                Zwischen dem Lernenden mit Profil{' '}
                <strong className="text-white uppercase tracking-wider">{mode.title}</strong>
                {' '}und dem DigiLernFlow-Begleiter wird folgende Partnerschaft vereinbart:
              </p>
            </div>

            {/* Profile Pills */}
            <div className="grid md:grid-cols-3 gap-4">
              <ProfilePill icon={FiCpu} label="Denkmodus" value={mode.title} color="blue" />
              <ProfilePill icon={FiTool} label="Tool-Stack" value={tool.title} color="emerald" />
              <ProfilePill icon={FiTarget} label="Mission" value={purpose.title} color="amber" />
            </div>

            {/* Learner Commitments */}
            <div className="space-y-6">
              <div>
                <h5 className="font-black text-base text-emerald-400 mb-1 flex items-center gap-2">
                  <SafeIcon icon={FiUser} />
                  Ich verpflichte mich als Lernender:
                </h5>
                <p className="text-slate-500 text-xs">
                  Diese Punkte sind mein persönlicher Beitrag zum gemeinsamen Erfolg.
                </p>
              </div>
              <ul className="space-y-3">
                {LEARNER_COMMITMENTS.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-4 bg-slate-800/40 rounded-2xl border border-slate-700/40"
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                    <span className="text-sm text-slate-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Sign Button */}
              <button
                onClick={() => setLearnerSigned(true)}
                disabled={learnerSigned}
                className={`w-full p-6 border-2 border-dashed rounded-[24px] transition-all flex items-center justify-center gap-3 ${
                  learnerSigned
                    ? 'bg-emerald-500/10 border-emerald-500 cursor-default'
                    : 'bg-emerald-500/5 border-emerald-500/40 hover:bg-emerald-500/10 cursor-pointer'
                }`}
              >
                {learnerSigned ? (
                  <>
                    <SafeIcon icon={FiCheck} className="text-emerald-400" />
                    <span className="font-black text-sm text-emerald-400">Selbstverpflichtung unterzeichnet</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiPenTool} className="text-slate-500" />
                    <span className="text-slate-400 font-bold text-sm">Ich akzeptiere diese Selbstverpflichtung</span>
                  </>
                )}
              </button>
            </div>

            {/* Info: Begleiter-Verpflichtungen per E-Mail */}
            <div className="p-5 bg-blue-500/5 border border-blue-500/20 rounded-2xl flex items-start gap-3">
              <SafeIcon icon={FiInfo} className="text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-blue-300 font-black text-xs uppercase tracking-widest mb-1">
                  Qualitätsverpflichtung des Begleiters
                </p>
                <p className="text-slate-400 text-xs leading-relaxed">
                  Die Verpflichtungen des Begleiters sowie der vollständige Lernplan werden dir
                  zusammen mit deiner Auswertung{' '}
                  <strong className="text-slate-300">per E-Mail</strong> zugesandt –
                  druckfertig und unterzeichnungsbereit.
                </p>
              </div>
            </div>

            {/* Combined Profile Note */}
            {combined && (
              <div className="p-5 bg-slate-800/40 rounded-2xl border border-slate-700/50">
                <div className="text-[10px] text-slate-400 uppercase font-black tracking-widest mb-1">
                  Dein kombiniertes Profil
                </div>
                <p className="text-white font-bold text-sm mb-0.5">{combined.headline}</p>
                <p className="text-xs text-slate-400 italic">"{combined.strategy}"</p>
              </div>
            )}

            {/* Signature Status */}
            <div className="flex gap-4 flex-wrap">
              <SignatureBadge label="Lernender" signed={learnerSigned} color="emerald" />
              <SignatureBadge label="Begleiter (per E-Mail)" signed={false} pending color="blue" />
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="flex flex-col items-center gap-4">
        {!learnerSigned && (
          <p className="text-slate-500 text-sm text-center">
            Bitte die Selbstverpflichtung unterzeichnen, um fortzufahren.
          </p>
        )}
        <button
          disabled={!learnerSigned}
          onClick={onNext}
          className={`group px-16 py-8 rounded-[32px] text-2xl font-black flex items-center gap-6 transition-all transform ${
            learnerSigned
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-2xl hover:scale-105 active:scale-95'
              : 'bg-slate-800 text-slate-500 cursor-not-allowed'
          }`}
        >
          Anmelden & Profil sichern
          <SafeIcon icon={FiLock} className={learnerSigned ? 'text-white' : 'text-slate-500'} />
        </button>
      </div>
    </motion.div>
  );
}

function ProfilePill({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-300',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-300',
  };
  return (
    <div className={`flex gap-3 p-4 rounded-2xl border ${colors[color]}`}>
      <SafeIcon icon={icon} className="mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-0.5">{label}</div>
        <div className="text-white font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}

function SignatureBadge({ label, signed, pending, color }) {
  const colors = {
    emerald: {
      active: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
      inactive: 'bg-slate-800/40 border-slate-700 text-slate-500',
    },
    blue: {
      active: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
      inactive: 'bg-slate-800/40 border-slate-700 text-slate-500',
    },
  };
  const c = colors[color] || colors.blue;
  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-black transition-all ${
        signed ? c.active : c.inactive
      }`}
    >
      <SafeIcon icon={signed ? FiCheck : FiPenTool} />
      <span>
        {label}: {signed ? 'Unterzeichnet' : pending ? 'Folgt per E-Mail' : 'Ausstehend'}
      </span>
    </div>
  );
}